import { Box, Flex, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type AdminAiUsageMonthDto,
  useAdminGetOrganizationAiUsageQuery,
} from "../../../autogen/bff-api";
import { formatNumber } from "../../../common/formatting/formatNumber";
import { useAiUsageFunctionLabels } from "./aiFeatureLabels";

interface Props {
  adminOrganizationId: string;
  organizationId: string;
}

export const AiUsageSection = ({ adminOrganizationId, organizationId }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useAdminGetOrganizationAiUsageQuery({
    organizationId: adminOrganizationId,
    orgId: organizationId,
  });

  return (
    <Box pt="4">
      <Flex justify="space-between" align="baseline" mb="1">
        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color="smMuted">
          {t("AI usage and model cost")}
        </Text>
      </Flex>
      {isLoading ? (
        <Spinner size="sm" color="smPrimary" />
      ) : isError || !data ? (
        <Text fontSize="sm" color="smError">
          {t("Could not load the AI usage.")}
        </Text>
      ) : (
        data.months.map((month) => <UsageMonth key={month.month} month={month} />)
      )}
    </Box>
  );
};

const UsageMonth = ({ month }: { month: AdminAiUsageMonthDto }) => {
  const { t, i18n } = useTranslation();
  const functionLabels = useAiUsageFunctionLabels();
  const kroner = (amount: string) => formatNumber(Number(amount));

  return (
    <Box mb="4">
      <Flex justify="space-between" align="baseline" gap="3" flexWrap="wrap">
        <Text fontWeight="semibold">{monthLabel(month.month, i18n.language)}</Text>
        <Text fontSize="sm">
          {t("Model cost: {{amount}} NOK", { amount: kroner(month.costNok) })}
          {" · "}
          {month.overageNok != null
            ? t("Overage: {{amount}} NOK", { amount: kroner(month.overageNok) })
            : t("No fair use set, so no overage is computed.")}
        </Text>
      </Flex>
      {month.functions.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No AI calls this month.")}
        </Text>
      ) : (
        <Table size="sm" mt="1">
          <Thead>
            <Tr>
              <Th>{t("AI function")}</Th>
              <Th isNumeric>{t("Calls")}</Th>
              <Th isNumeric>{t("Tokens in / out")}</Th>
              <Th isNumeric>{t("Cost (NOK)")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {month.functions.map((row) => (
              <Tr key={row.function}>
                <Td>{functionLabels[row.function]}</Td>
                <Td isNumeric>{formatNumber(row.calls)}</Td>
                <Td isNumeric>
                  {row.callsWithoutTokens === row.calls
                    ? t("tokens unknown")
                    : `${formatNumber(row.inputTokens)} / ${formatNumber(row.outputTokens)}`}
                  {row.callsWithoutTokens > 0 && row.callsWithoutTokens < row.calls && (
                    <Text as="span" color="smMuted">
                      {" · "}
                      {t("{{uncounted}} of {{calls}} calls without tokens", {
                        uncounted: formatNumber(row.callsWithoutTokens),
                        calls: formatNumber(row.calls),
                      })}
                    </Text>
                  )}
                </Td>
                <Td isNumeric>{kroner(row.costNok)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
};

const monthLabel = (isoMonth: string, language: string): string => {
  const [year, month] = isoMonth.split("-").map(Number);
  return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "nb-NO", { month: "long", year: "numeric" }).format(
    new Date(year, month - 1, 1),
  );
};
